import * as Icons from '../icons';
import { FileUploadProps } from './FileUpload.types';
import { FileInstructions, FileStatusMessage } from './components';
import {
  addValidationError,
  defaultInstructions,
  defaultLabels,
  defaultValidation,
  getContentWrapperStyles,
  getDropzoneStyles,
  getDropzoneTextStyles,
  getLabel,
  getTextColor,
  getUploadIconStyles,
} from './utils';
import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { ChangeEvent, DragEvent, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const FileUpload = ({
  onFileSelect,
  onValidationResult,
  loading = false,
  error,
  success,
  validation = defaultValidation,
  labels = defaultLabels,
  className,
  sx,
  multiple = false,
  readOnly = false,
  showInstructions = true,
  instructions = defaultInstructions,
}: FileUploadProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const validateFile = useCallback(
    (file: File) => {
      const errors: string[] = [];
      const warnings: string[] = [];

      // Check file size
      if (validation.maxFileSize && file.size > validation.maxFileSize) {
        addValidationError(labels.errorFileSize, defaultLabels.errorFileSize || '', errors);
      }

      // Check file type and extension
      if (validation.acceptedTypes && !validation.acceptedTypes.includes(file.type)) {
        addValidationError(labels.errorFileType, defaultLabels.errorFileType || '', errors);
      } else if (validation.allowedExtensions) {
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!validation.allowedExtensions.includes(fileExtension)) {
          addValidationError(labels.errorFileType, defaultLabels.errorFileType || '', errors);
        }
      }

      const result = {
        isValid: errors.length === 0,
        errors,
        warnings,
      };

      onValidationResult(result);
      return result.isValid;
    },
    [validation, onValidationResult, labels]
  );

  const handleFiles = useCallback(
    (files: FileList) => {
      if (!multiple && files.length > 1) {
        onValidationResult({
          isValid: false,
          errors: [getLabel(labels.errorMultipleFiles, defaultLabels.errorMultipleFiles || '')],
          warnings: [],
        });
        return;
      }

      const file = files[0];
      if (validateFile(file)) {
        onFileSelect(file);
      }
    },
    [multiple, validateFile, onFileSelect, labels]
  );

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      if (!readOnly) {
        setIsDragging(false);
        handleFiles(e.dataTransfer.files);
        // Reset input value to allow selecting the same file again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [handleFiles, readOnly]
  );

  const handleDragOver = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      if (!readOnly) {
        setIsDragging(true);
      }
    },
    [readOnly]
  );

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleClick = useCallback(() => {
    if (!readOnly) {
      fileInputRef.current?.click();
    }
  }, [readOnly]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        handleFiles(e.target.files);
        // Reset input value to allow selecting the same file again
        e.target.value = '';
      }
    },
    [handleFiles]
  );

  return (
    <Box
      className={className}
      sx={getDropzoneStyles(error, isDragging, readOnly && !loading, sx)}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={handleClick}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={validation.acceptedTypes?.join(',')}
        onChange={handleChange}
        style={{ display: 'none' }}
        multiple={multiple}
        data-testid="file-input"
        disabled={readOnly}
      />

      <Box sx={getContentWrapperStyles()}>
        {loading ? (
          <Stack direction="column" alignItems="center">
            <Icons.UploadFile sx={getUploadIconStyles()} />
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              {t('blueprints:wizard.fields.powerProfileGeneration.modeInfo.uploading')}
            </Typography>
            <LinearProgress variant="indeterminate" color="primary" sx={{ width: 250 }} />
          </Stack>
        ) : (
          <>
            <Icons.UploadFile sx={getUploadIconStyles()} />
            <Typography
              variant="body1"
              color={getTextColor(readOnly, 'primary')}
              gutterBottom
              sx={getDropzoneTextStyles('main')}
            >
              {getLabel(labels.dropzone, defaultLabels.dropzone || '')}
            </Typography>
            <Typography
              variant="body2"
              color={getTextColor(readOnly, 'disabled')}
              sx={getDropzoneTextStyles('secondary')}
            >
              {getLabel(labels.fileSize, defaultLabels.fileSize || '')}
            </Typography>
            <Typography
              variant="body2"
              color={getTextColor(readOnly, 'disabled')}
              sx={getDropzoneTextStyles('secondary')}
            >
              {getLabel(labels.fileTypes, defaultLabels.fileTypes || '')}
            </Typography>
            {showInstructions && instructions && (
              <FileInstructions
                instructions={instructions}
                readOnly={readOnly}
                title={getLabel(labels.instructionsTitle, defaultLabels.instructionsTitle || '')}
              />
            )}
            {error && <FileStatusMessage type="error" message={error} />}
            {success && !error && (
              <FileStatusMessage type="success" message={getLabel(labels.success, defaultLabels.success || '')} />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};
